import type { IntegrationIssue } from "src/common/service/fusionHub/types.ts";

// The action the portal can offer for an issue. "reconnect" re-runs the OAuth
// flow; "retrySync" triggers POST /integrations/sync. Issues without a mapped
// action still render a banner, just without a CTA.
export type IssueActionKind = "reconnect" | "retrySync" | "recheckPermissions";

// All message fields are i18next keys (English), translated in sv.json. The
// remove-unused-translations scanner matches raw string occurrences, so keys
// living in this map are safe from cleanup.
export type IssueTreatment = {
	indicatorMessage: string;
	bannerTitle: string;
	bannerText: string;
	actionKind?: IssueActionKind;
	// WHY: a warning means syncing still works, so it must not flag the data source as needing attention.
	severity: "error" | "warning";
};

// FusionHub names these issues `<source>_authentication_expired` (fortnox,
// visma_eaccounting, exact_online today, more to follow in FusionHub#3573).
// Matching the suffix keeps the portal working for sources added later.
const AUTHENTICATION_EXPIRED_SUFFIX = "_authentication_expired";

const authenticationExpiredTreatment: IssueTreatment = {
	indicatorMessage: "Connection needs attention. Reconnect to resume syncing.",
	bannerTitle: "Connection needs attention",
	bannerText: "Reconnect the data source to resume syncing.",
	actionKind: "reconnect",
	severity: "error",
};

// WHY suffix: FusionHub adds one `<source>_permission_missing` type per source (FusionHub#3792).
const PERMISSION_MISSING_SUFFIX = "_permission_missing";

const permissionMissingTreatment: IssueTreatment = {
	indicatorMessage:
		"Some data can't be fetched. Grant the API user more permissions in the source system.",
	bannerTitle: "Some data can't be fetched",
	bannerText:
		"The API key lacks permissions for the data below. If you use this data, grant the API user more permissions in the source system, then check the permissions again.",
	actionKind: "recheckPermissions",
	severity: "warning",
};

// Reconnecting cannot fix a missing Visma module entitlement, so this issue
// gets its own copy and a retry-sync CTA instead of "Reconnect" (portal#4230).
const treatmentsByType: Record<string, IssueTreatment> = {
	visma_eaccounting_module_access_missing: {
		indicatorMessage:
			"Missing access rights in Visma eAccounting. Grant the Standard API module, then retry the sync.",
		bannerTitle: "Missing access rights in Visma eAccounting",
		bannerText:
			"Grant the integration user access to the Standard API module in Visma eAccounting, then retry the sync.",
		actionKind: "retrySync",
		severity: "error",
	},
	// Only the customer can fix this: authorise the App Connector's user group
	// for the administrations in AFAS, then retry the sync.
	afas_administration_access_missing: {
		indicatorMessage:
			"Missing administration access in AFAS. Grant the App Connector's user group access, then retry the sync.",
		bannerTitle: "Missing administration access in AFAS",
		bannerText:
			"Grant the App Connector's user group authorisation for the administrations in AFAS, then retry the sync.",
		actionKind: "retrySync",
		severity: "error",
	},
};

// An unmapped issue type must still surface — rendering nothing is how the
// 22-duplicate-integrations incident started.
const fallbackTreatment: IssueTreatment = {
	indicatorMessage: "The data source has a problem that needs attention.",
	bannerTitle: "Connection needs attention",
	bannerText:
		"The data source has a problem that needs attention. Contact support if it persists.",
	severity: "error",
};

export const getIssueTreatment = (issue: IntegrationIssue): IssueTreatment => {
	if (issue.type.endsWith(AUTHENTICATION_EXPIRED_SUFFIX)) {
		return authenticationExpiredTreatment;
	}
	if (issue.type.endsWith(PERMISSION_MISSING_SUFFIX)) {
		return permissionMissingTreatment;
	}
	return treatmentsByType[issue.type] ?? fallbackTreatment;
};

export type TreatedIssue = {
	issue: IntegrationIssue;
	treatment: IssueTreatment;
};

/** Finds the issue that blocks syncing; warnings are left to {@link findWarningIssue}. */
export const findTreatedIssue = (
	issues: IntegrationIssue[] | undefined,
): TreatedIssue | undefined => {
	const blocking = (issues ?? []).filter((candidate) => {
		return getIssueTreatment(candidate).severity === "error";
	});
	// Prefer an issue the customer can act on over a fallback-only one.
	const issue =
		blocking.find((candidate) => {
			return getIssueTreatment(candidate) !== fallbackTreatment;
		}) ?? blocking[0];
	if (!issue) {
		return undefined;
	}
	return { issue, treatment: getIssueTreatment(issue) };
};

/** Finds an issue where syncing works but some data is missing, e.g. missing API permissions. */
export const findWarningIssue = (
	issues: IntegrationIssue[] | undefined,
): TreatedIssue | undefined => {
	const issue = issues?.find((candidate) => {
		return getIssueTreatment(candidate).severity === "warning";
	});
	if (!issue) {
		return undefined;
	}
	return { issue, treatment: getIssueTreatment(issue) };
};
